import React, {useContext, useRef} from 'react'
import Axios from 'axios'
import AppContext from '../../appContext'
import LimeController from '../../controllers/LimeController'
import LimeProgramListResult from '../../controllers/LimeProgramListResult'
import PagedSearchTable, {PagedTableFunctions} from "../../components/PagedSearchTable"
import Warning, {useWarningState} from "../program/Warning"
import {showSuccessOrFailed} from "../../Snacks"
import {dateFormat} from "../../date"
import PermissionEnum from "../../controllers/PermissionEnum"
import Tabs from "../../components/Tabs"
import {useNavigate} from "react-router-dom"
import Success from "../../components/Success"
import Failed from "../../components/Failed"
import {calculateLabInfoRow, convertToLabInputRow} from './limeCalcs'

const LimingPrograms: React.FC = () => {
    const context = useContext(AppContext)

    return (
        <Tabs tabs={[
            {
                header: () => context.word('current'),
                key: 'current',
                content: <LimeProgramList key="current1" />
            },
            {
                header: () => context.word('archives'),
                key: 'archives',
                content: <LimeProgramArchives key="archive1" />
            },
        ]} />
    )
}
export default LimingPrograms

const LimeProgramDownload: React.FC<{
    id: number;
    downloadPdf: (id: number) => void;
    downloadExcel: (id: number) => void;
}> = (props) => {
    return (
        <div className="flex">
            <div className="flex ml-3">
                <div
                    className="w-[48px] h-[39px] bg-no-repeat cursor-pointer hover:bg-gray-200"
                    style={{ backgroundImage: 'url(/images/pdf.png)', backgroundSize: '48px 39px' }}
                    onClick={(e) => {
                        e.stopPropagation()
                        props.downloadPdf(props.id)
                    }}
                />
            </div>
            <div className="flex mx-1">
                <div
                    className="w-[48px] h-[39px] bg-no-repeat cursor-pointer hover:bg-gray-200"
                    style={{ backgroundImage: 'url(/images/excel.png)', backgroundSize: '48px 39px' }}
                    onClick={(e) => {
                        e.stopPropagation()
                        props.downloadExcel(props.id)
                    }}
                />
            </div>
        </div>
    )
}

const LimeProgramList: React.FC = () => {
    const navigate = useNavigate()
    const context = useContext(AppContext)
    const pagedTableRef = useRef<PagedTableFunctions<LimeProgramListResult>>()
    const warningState = useWarningState<number>(-1)

    function rowRemove(id: number) {
        showSuccessOrFailed(context, LimeController.archiveProgram({ id }), 'successfully_deleted', 'failed_to_delete').then(() => {
            pagedTableRef.current?.refresh()
        })
    }

    function rowRemoveWarning(id: number) {
        warningState.show(`${context.word('removing')}?`, id)
    }

    function programClick(row: LimeProgramListResult) {
        navigate(`/liming/programs/${row.id}`)
    }

    async function downloadPdf(id: number) {
        context.showSnack(<Success title={context.word('download_start_shortly')} />)
        try {
            const exportRequest = await buildExportRequest(id)
            if (!exportRequest) {
                context.showSnack(<Failed title={context.word('failed_to_load')} />)
                return
            }

            const response = await Axios.post('/lime/getProgramPdf', exportRequest, {
                responseType: 'blob'
            })
            const url = window.URL.createObjectURL(new Blob([response.data]))
            const link = document.createElement('a')
            link.href = url
            link.setAttribute('download', `LimeProgram_${exportRequest.name || 'export'}.pdf`)
            document.body.appendChild(link)
            link.click()
            link.remove()
            window.URL.revokeObjectURL(url)
        } catch (error) {
            context.showSnack(<Failed title={context.word('export_failed')} />)
        }
    }

    async function downloadExcel(id: number) {
        context.showSnack(<Success title={context.word('download_start_shortly')} />)
        try {
            const exportRequest = await buildExportRequest(id)
            if (!exportRequest) {
                context.showSnack(<Failed title={context.word('failed_to_load')} />)
                return
            }

            const response = await Axios.post('/lime/getProgramExcel', exportRequest, {
                responseType: 'blob'
            })
            const url = window.URL.createObjectURL(new Blob([response.data]))
            const link = document.createElement('a')
            link.href = url
            link.setAttribute('download', `LimeProgram_${exportRequest.name || 'export'}.xlsx`)
            document.body.appendChild(link)
            link.click()
            link.remove()
            window.URL.revokeObjectURL(url)
        } catch (error) {
            context.showSnack(<Failed title={context.word('export_failed')} />)
        }
    }

    async function buildExportRequest(programId: number) {
        try {
            const program = await LimeController.getProgram(programId)
            if (!program || !program.templateData) return null

            const exportLands = program.lands.map(land => {
                const labInputs = land.labInputs.rows.map(row => convertToLabInputRow(row))
                const calculatedRows = labInputs.map(labInput =>
                    calculateLabInfoRow(labInput, program.templateData)
                )
                return {
                    landName: land.landName,
                    labInputs: labInputs,
                    calculatedRows: calculatedRows
                }
            })

            return {
                id: program.id,
                name: program.name,
                clientName: program.clientName,
                farmName: program.farmName,
                templateName: program.limeTemplateName,
                templateData: program.templateData,
                lands: exportLands
            }
        } catch {
            return null
        }
    }

    return <>
        <Warning state={warningState} onYes={rowRemove} />

        <div className="btn bg-primary m-2"
            onClick={() => navigate('/liming/programs/new')}>{context.word('new_program')}</div>

        <PagedSearchTable<LimeProgramListResult>
            call={LimeController.programsPaged}
            rowClick={programClick}
            componentRef={pagedTableRef}
            columns={[
                {
                    header: context.word('name'),
                    row: (item) => item.name
                },
                {
                    header: context.word('farm'),
                    row: (item) => (
                        <>
                            <div className="text-l text-gray-800">{item.farmName}</div>
                            <div className="text-xs text-gray-500">{item.clientName}</div>
                        </>
                    )
                },
                {
                    header: context.word('template'),
                    row: (item) => item.templateName
                },
                {
                    header: context.word('created'),
                    row: (item) => (
                        <>
                            <div className="text-l text-gray-800">{item.createdBy}</div>
                            <div className="text-xs text-gray-500">{dateFormat(item.createdOn)}</div>
                        </>
                    )
                },
                {
                    header: context.word('updated'),
                    row: (item) => dateFormat(item.updatedOn)
                },
                {
                    header: context.word('submitted'),
                    row: (item) =>
                        <>
                            <div className="text-l text-gray-800">{item.submittedBy || '-'}</div>
                            <div className="text-xs text-gray-500">{item.submittedOn ? dateFormat(item.submittedOn) : ''}</div>
                        </>
                },
                {
                    header: context.word('approved'),
                    row: (item) =>
                        <>
                            <div className="text-l text-gray-800">{item.approvedBy || '-'}</div>
                            <div className="text-xs text-gray-500">{item.approvedOn ? dateFormat(item.approvedOn) : ''}</div>
                        </>
                },
                {
                    header: context.word('actions'),
                    row: (item) =>
                        <>
                            <div className="flex items-center">
                                {context.hasPermission(PermissionEnum.LimingProgramsPermission)
                                    ? <>
                                        <span className="underline" onClick={event => {
                                            event.stopPropagation()
                                            rowRemoveWarning(item.id)
                                        }}>{context.word('delete')}</span>
                                    </>
                                    : null
                                }

                                {item.approvedOn !== null && (
                                    <span onClick={e => e.stopPropagation()}>
                                        <LimeProgramDownload
                                            id={item.id}
                                            downloadPdf={downloadPdf}
                                            downloadExcel={downloadExcel}
                                        />
                                    </span>
                                )}
                            </div>
                        </>
                }
            ]}
            keyExtractor={i => i.id}
        />
    </>
}

const LimeProgramArchives: React.FC = () => {
    const context = useContext(AppContext)
    const pagedTableRef = useRef<PagedTableFunctions<LimeProgramListResult>>()
    const warningState = useWarningState<number>(-1)

    function rowRestore(id: number) {
        showSuccessOrFailed(context, LimeController.restoreProgram({ id }), 'successfully_restored', 'failed_to_restore').then(() => {
            pagedTableRef.current?.refresh()
        })
    }

    function rowRemoveWarning(id: number) {
        warningState.show(`${context.word('restore')}?`, id)
    }

    return <>
        <Warning state={warningState} onYes={rowRestore} />

        <PagedSearchTable<LimeProgramListResult>
            instantSearch={true}
            call={LimeController.programArchives}
            componentRef={pagedTableRef}
            columns={[
                {
                    header: context.word('name'),
                    row: (item) => item.name
                },
                {
                    header: context.word('farm'),
                    row: (item) => (
                        <>
                            <div className="text-l text-gray-800">{item.farmName}</div>
                            <div className="text-xs text-gray-500">{item.clientName}</div>
                        </>
                    )
                },
                {
                    header: context.word('template'),
                    row: (item) => item.templateName
                },
                {
                    header: context.word('created'),
                    row: (item) => (
                        <>
                            <div className="text-l text-gray-800">{item.createdBy}</div>
                            <div className="text-xs text-gray-500">{dateFormat(item.createdOn)}</div>
                        </>
                    )
                },
                {
                    header: context.word('updated'),
                    row: (item) => dateFormat(item.updatedOn)
                },
                {
                    header: context.word('submitted'),
                    row: (item) =>
                        <>
                            <div className="text-l text-gray-800">{item.submittedBy || '-'}</div>
                            <div className="text-xs text-gray-500">{item.submittedOn ? dateFormat(item.submittedOn) : ''}</div>
                        </>
                },
                {
                    header: context.word('approved'),
                    row: (item) =>
                        <>
                            <div className="text-l text-gray-800">{item.approvedBy || '-'}</div>
                            <div className="text-xs text-gray-500">{item.approvedOn ? dateFormat(item.approvedOn) : ''}</div>
                        </>
                },
                {
                    header: context.word('actions'),
                    row: (item) =>
                        <>
                            <div className="flex items-center">
                                {context.hasPermission(PermissionEnum.LimingProgramsPermission)
                                    ? <span className="underline mx-3" onClick={event => {
                                        event.stopPropagation()
                                        rowRemoveWarning(item.id)
                                    }}>{context.word('restore')}</span>
                                    : null
                                }
                            </div>
                        </>
                }
            ]}
            keyExtractor={i => i.id} />
    </>
}
